import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownItemProps,
  DropdownProps,
  DropdownSeparator,
  DropdownTrigger,
  IconButton,
  Stack,
} from 'component-library';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ChartBenchmark } from '@/components/Charts/components/ChartBenchmark';
import { ChartZoom, ZoomValue } from '@/components/Charts/components/ChartZoom';
import { ItemizedGraphType } from '@/components/Charts/components/ItemizedGraphType';
import { GraphTypeValue } from '@/components/Charts/hooks/useChartType';
import { useToggle } from '@/hooks/useToggle';
import { Logger } from '@/lib/logs/logger';

type GraphEditProps = {
  indicator?: GraphTileType;
  graphType?: GraphTypeValue;
  setGraphType?: (newValue: GraphTypeValue) => void;
  setZoom?: Dispatch<SetStateAction<ZoomValue>>;
  zoom?: ZoomValue;
  selectedBenchmark?: string | null;
  setSelectedBenchmark?: (id: string | null) => void;
  onOpenBenchmarkDialog?: () => void;
} & DropdownProps;

export const ChartEditDropdown = ({
  graphType,
  setGraphType,
  indicator,
  setZoom,
  zoom,
  selectedBenchmark,
  setSelectedBenchmark,
  onOpenBenchmarkDialog,
  ...delegated
}: GraphEditProps) => {
  const { t } = useTranslation();

  if (!setZoom && !zoom && !setGraphType && !graphType && !setSelectedBenchmark) {
    Logger.error('ChartEditDropdown: No options to edit were provided.');
    return null;
  }

  return (
    <Dropdown>
      <DropdownTrigger asChild {...delegated}>
        <IconButton className='border text-primary-100 px-3'>
          <i className='ri-pencil-line' />
        </IconButton>
      </DropdownTrigger>
      <DropdownContent align='end' className='w-[320px] mt-2'>
        <EditSection
          label={t('graph.chartStyle.title')}
          disable={!setGraphType || !graphType}
          data-testid='chart-type-section'
        >
          <ItemizedGraphType onChange={setGraphType} value={graphType} indicator={indicator} />
        </EditSection>
        <DropdownSeparator />
        <EditSection label={t('graph.zoom.title')} disable={!setZoom || !zoom} data-testid='chart-zoom-section'>
          <ChartZoom setZoom={setZoom} zoom={zoom} />
        </EditSection>
        <DropdownSeparator />
        <EditSection
          label={t('graph.benchmark.title')}
          disable={!setSelectedBenchmark}
          data-testid='chart-benchmark-section'
        >
          <ChartBenchmark
            selectedBenchmark={selectedBenchmark}
            setSelectedBenchmark={setSelectedBenchmark}
            onOpenDialog={onOpenBenchmarkDialog}
            indicatorType={indicator}
          />
        </EditSection>
      </DropdownContent>
    </Dropdown>
  );
};

const EditSection = ({
  label,
  children,
  disable,
  ...delegated
}: {
  label: string;
  children: ReactNode;
  disable: boolean;
} & DropdownItemProps) => {
  const [isOpen, toggleIsOpen] = useToggle(false);

  const handleOpenChange = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    toggleIsOpen();
  };

  return (
    <DropdownItem
      onSelect={handleOpenChange}
      className='data-[highlighted]:bg-transparent py-2'
      disabled={disable}
      {...delegated}
    >
      <Stack className='w-full'>
        <Stack direction='row' className='justify-between w-full'>
          {label}
          {isOpen ? <i className='ri-arrow-up-s-line' /> : <i className='ri-arrow-down-s-line' />}
        </Stack>
        {isOpen && children}
      </Stack>
    </DropdownItem>
  );
};
